import React from "react";
import { cn } from "@/lib/utils";

/**
 * EmptyState — unified empty state with icon, title, message, and optional CTA.
 * Instagram-esque centered layout; on mobile the CTA becomes full-width.
 *
 * Usage:
 *   <EmptyState
 *     icon={FileText}
 *     title="No briefs yet"
 *     message="Kick off your first pitch to get this board moving."
 *     cta={{ label: "Create brief", onClick: () => setOpen(true) }}
 *   />
 */
export function EmptyState({
  icon: Icon,
  title,
  message,
  cta,
  secondary,
  className,
  compact = false,
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center",
        compact ? "py-8 px-4" : "py-16 px-6",
        className
      )}
    >
      {Icon && (
        <div
          className={cn(
            "mb-4 flex items-center justify-center rounded-full",
            "bg-[var(--s1)] border border-[var(--bdr)]",
            compact ? "w-12 h-12" : "w-16 h-16"
          )}
        >
          <Icon className={cn(compact ? "w-5 h-5" : "w-7 h-7", "text-[var(--t3)]")} />
        </div>
      )}
      {title && (
        <h3
          className={cn(
            "font-heading font-semibold text-[var(--t1)]",
            compact ? "text-base mb-1" : "text-lg mb-2"
          )}
        >
          {title}
        </h3>
      )}
      {message && (
        <p
          className={cn(
            "text-[var(--t3)] max-w-sm",
            compact ? "text-xs" : "text-sm"
          )}
        >
          {message}
        </p>
      )}
      {(cta || secondary) && (
        <div className={cn("mt-5 flex flex-col sm:flex-row gap-2 w-full sm:w-auto")}>
          {cta && (
            <button
              onClick={cta.onClick}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                "bg-blue-600 hover:bg-blue-500 text-white",
                "w-full sm:w-auto"
              )}
            >
              {cta.label}
            </button>
          )}
          {secondary && (
            <button
              onClick={secondary.onClick}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                "border border-[var(--bdr)] text-[var(--t2)] hover:bg-[var(--s1)]",
                "w-full sm:w-auto"
              )}
            >
              {secondary.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default EmptyState;
